import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { motion, useScroll, useTransform, animate } from "framer-motion";
import { ArrowRight, Clock, Eye, ShieldCheck } from "lucide-react";
import { Reveal, MaskLine } from "../components/Reveal";
import { PrebookForm } from "../components/PrebookForm";
import { IMAGES, LIFESTYLE, CHAPTERS, STEPS, SPECS } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Counter = ({ value }) => {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    const controls = animate(prev.current, value, {
      duration: 1.4,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    prev.current = value;
    return () => controls.stop();
  }, [value]);
  return <span>{display}</span>;
};

const scrollToForm = () => {
  document.getElementById("prebook")?.scrollIntoView({ behavior: "smooth" });
};

export default function Landing() {
  const [total, setTotal] = useState(30);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const loadStats = () => {
    axios.get(`${API}/stats`).then((r) => setTotal(r.data.total)).catch(() => {});
  };
  useEffect(() => { loadStats(); }, []);

  return (
    <div className="relative overflow-x-hidden bg-[#FBFBF9]">
      <div className="noise-overlay" />

      {/* NAV */}
      <header className="fixed top-0 left-0 z-50 w-full">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-6 md:px-12">
          <a href="/" className="flex items-center" data-testid="logo-link">
            <img src={IMAGES.logoDark} alt="Transcendence" className="h-8 w-auto md:h-9" />
          </a>
          <button
            onClick={scrollToForm}
            data-testid="nav-prebook-btn"
            className="group inline-flex items-center gap-2 rounded-full border border-[#0A0A0A]/15 bg-white/70 px-5 py-2.5 text-xs font-medium tracking-wide text-[#0A0A0A] backdrop-blur-md transition-colors hover:bg-[#0A0A0A] hover:text-white"
          >
            Pre-book
            <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </header>

      {/* HERO */}
      <section ref={heroRef} className="relative mx-auto max-w-[1400px] px-6 pt-36 pb-20 md:px-12 md:pt-44">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }}
              className="eyebrow mb-8"
              style={{ color: "#4a4a4a" }}
            >
              Pre-launch · India · Precision Watch Winders
            </motion.p>
            <h1 className="font-serif-display text-[13vw] font-light leading-[0.92] tracking-tighter text-[#0A0A0A] sm:text-7xl md:text-8xl">
              <MaskLine delay={0.15}>Precision</MaskLine>
              <MaskLine delay={0.28}>motion.</MaskLine>
              <MaskLine delay={0.41} className="italic text-[#B59969]">Zero compromise.</MaskLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.9 }}
              className="mt-8 max-w-md text-base leading-relaxed text-[#525252] md:text-lg"
            >
              A precision-engineered watch winder built around a Japanese Mabuchi motor and a precise nylon gear assembly — calibrated rotation for automatic watches, designed and priced for Indian collectors.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-6"
            >
              <button
                onClick={scrollToForm}
                data-testid="hero-prebook-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-[#0A0A0A] px-8 py-4 text-sm font-medium tracking-wide text-white transition-colors hover:bg-[#333]"
              >
                Pre-book at zero cost
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <div className="flex items-center gap-3">
                <div className="flex h-2 w-2">
                  <span className="absolute h-2 w-2 animate-ping rounded-full bg-[#B59969] opacity-75" />
                  <span className="h-2 w-2 rounded-full bg-[#B59969]" />
                </div>
                <span className="eyebrow">Shipping starts in September</span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.7 }}
              data-testid="hero-pricing"
              className="mt-6 flex flex-wrap items-baseline gap-x-3 gap-y-2"
            >
              <span className="eyebrow">Expected launch price</span>
              <span className="font-serif-display text-lg text-[#A3A3A3] line-through [font-variant-numeric:lining-nums]">₹24,999</span>
              <span className="font-serif-display text-2xl text-[#0A0A0A] [font-variant-numeric:lining-nums] md:text-3xl">₹22,999</span>
              <span className="rounded-full bg-[#B59969]/15 px-3 py-1 font-mono-accent text-[10px] tracking-wider text-[#8a6a2f]">8% LAUNCH OFFER</span>
            </motion.div>

          </div>

          <div className="lg:col-span-5">
            <motion.div
              style={{ y: imgY }}
              initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.35, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="img-zoom relative overflow-hidden rounded-[2px] bg-[#F3F3F1]"
            >
              <motion.img
                style={{ scale: imgScale }}
                src={IMAGES.hero}
                alt="Transcendence watch winder in blue holding an automatic watch, styled on a shelf"
                className="h-[380px] w-full object-cover object-[35%_center] md:h-[560px]"
              />
              <div className="absolute bottom-4 left-4 rounded-full bg-white/80 px-4 py-2 backdrop-blur-md">
                <span className="font-mono-accent text-[10px] tracking-wider text-[#0A0A0A]">CALIBRATED ROTATION · 650–1950 TPD</span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* live counter strip */}
        <Reveal delay={0.2} className="mt-16 border-t border-[#0A0A0A]/8 pt-8">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
            <span className="font-serif-display text-6xl font-light [font-variant-numeric:lining-nums_tabular-nums] text-[#0A0A0A] md:text-7xl">
              <Counter value={total} />
            </span>
            <div>
              <p className="font-serif-display text-2xl italic text-[#525252]">collectors have already reserved theirs</p>
              <p className="eyebrow mt-1">Join the waitlist · Reserve your colour before general release</p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[#0A0A0A]/8 py-6">
        <div className="relative flex overflow-hidden">
          <div className="marquee-track">
            {[0, 1].map((k) => (
              <span key={k} className="flex items-center">
                {["Mabuchi Motor", "Nylon Gear Assembly", "650–1950 TPD", "Ultra-Quiet", "Adjustable Cushion", "Made for India", "Zero-Cost Pre-Booking"].map((t) => (
                  <span key={t} className="flex items-center font-serif-display text-2xl font-light text-[#0A0A0A] md:text-3xl">
                    <span className="px-8">{t}</span>
                    <span className="text-[#B59969]">◆</span>
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* WHY A WATCH WINDER */}
      <section className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40">
        <Reveal>
          <p className="eyebrow mb-6">Why a watch winder</p>
          <h2 className="max-w-2xl font-serif-display text-4xl font-light leading-tight tracking-tight text-[#0A0A0A] md:text-6xl">
            Kept alive, <span className="italic text-[#B59969]">not stored away.</span>
          </h2>
          <p className="mt-6 max-w-md text-[#525252] md:text-lg">
            An automatic watch is meant to be worn and to move. A winder keeps it running as if it never left your wrist.
          </p>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden rounded-[2px] border border-[#0A0A0A]/8 bg-[#0A0A0A]/8 md:grid-cols-3">
          {[
            { icon: Clock, n: "01", title: "Always Ready", body: "You don't have to reset the watch every time you pick it up. Avoid repeatedly resetting date and time after the power reserve runs down." },
            { icon: Eye, n: "02", title: "Secure Display", body: "Store and showcase your timepiece beautifully — a display piece on the shelf, rather than something hidden away in a drawer." },
            { icon: ShieldCheck, n: "03", title: "Longevity", body: "Controlled, intermittent motion keeps lubricant evenly distributed, preventing friction and stagnation to extend the movement's life." },
          ].map((c, i) => (
            <Reveal key={c.n} delay={i * 0.1} className="h-full">
              <div className="flex h-full flex-col gap-8 bg-[#FBFBF9] p-8 md:p-10">
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#0A0A0A]/12 text-[#0A0A0A]">
                    <c.icon size={18} strokeWidth={1.5} />
                  </span>
                  <span className="font-mono-accent text-xs text-[#B59969]">{c.n}</span>
                </div>
                <div>
                  <h3 className="font-serif-display text-2xl font-medium text-[#0A0A0A] md:text-3xl">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#525252]">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANIFESTO / CHAPTERS */}
      <section className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow mb-6">Engineering</p>
              <h2 className="font-serif-display text-4xl font-light leading-tight tracking-tight text-[#0A0A0A] md:text-6xl">
                Designed as <span className="italic text-[#B59969]">one system.</span>
              </h2>
              <p className="mt-6 max-w-sm text-[#525252] md:text-lg">
                Every part is built to work with the next — a Japanese motor and precise nylon gear assembly driving the rotation, an adjustable cushion holding the watch steady, and calibrated settings tying it all to how your movement actually behaves.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <div className="flex flex-col divide-y divide-[#0A0A0A]/8">
              {CHAPTERS.map((c, i) => (
                <Reveal key={c.n} delay={i * 0.08} className="relative py-10 first:pt-0">
                  <span className="pointer-events-none absolute -top-2 -left-2 -z-10 font-serif-display text-8xl text-[#0A0A0A]/[0.04] md:text-9xl">
                    {c.n}
                  </span>
                  <div className="flex items-baseline gap-4">
                    <span className="font-mono-accent text-xs text-[#B59969]">{c.n}</span>
                    <div>
                      <h3 className="font-serif-display text-2xl font-medium text-[#0A0A0A] md:text-3xl">{c.title}</h3>
                      <p className="mt-3 max-w-md text-[#525252]">{c.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DARK SHOWCASE */}
      <section className="bg-[#0A0A0A] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <p className="eyebrow mb-4 text-[#8a8a8a]">In detail</p>
            <h2 className="max-w-2xl font-serif-display text-4xl font-light leading-tight tracking-tight text-white md:text-6xl">
              Every component, <span className="italic text-[#B59969]">deliberate.</span>
            </h2>
          </Reveal>
          <div className="mt-14 flex flex-col items-center gap-6 md:gap-8">
            {[
              { src: IMAGES.motorBanner, alt: "Mabuchi motor and nylon gear assembly — precision engineered" },
              { src: IMAGES.controlBanner, alt: "Smart control panel — dual power, 3 direction modes, 5 TPD settings" },
              { src: IMAGES.linkBanner, alt: "Transcendence Link feature — connect up to 4 winders on one AC power source" },
              { src: IMAGES.cushionBanner, alt: "Adjustable cushion — secure hold and soft flexible fit" },
            ].map((b, i) => (
              <Reveal key={i} delay={i * 0.08} className="img-zoom w-full overflow-hidden rounded-[3px] border border-white/10 md:max-w-4xl">
                <img src={b.src} alt={b.alt} loading="lazy" className="w-full object-cover" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LIFESTYLE GALLERY */}
      <section className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40">
        <Reveal>
          <p className="eyebrow mb-6">In your world</p>
          <h2 className="max-w-2xl font-serif-display text-4xl font-light leading-tight tracking-tight text-[#0A0A0A] md:text-6xl">
            A place on <span className="italic text-[#B59969]">every shelf.</span>
          </h2>
          <p className="mt-6 max-w-md text-[#525252] md:text-lg">
            Compact enough for a desk, refined enough for a study — Transcendence keeps your automatic wound and ready, wherever the day begins.
          </p>
        </Reveal>
        <div className="mt-14 grid grid-cols-2 auto-rows-[10rem] gap-3 md:grid-cols-4 md:auto-rows-[15rem] md:gap-4">
          <Reveal className="img-zoom col-span-2 row-span-2 overflow-hidden rounded-[3px]">
            <img src={LIFESTYLE.feature} alt="Transcendence watch winder on marble with a Tissot PRX automatic" loading="lazy" className="h-full w-full object-cover" />
          </Reveal>
          {LIFESTYLE.tiles.map((src, i) => (
            <Reveal key={i} delay={i * 0.06} className="img-zoom overflow-hidden rounded-[3px]">
              <img src={src} alt="Transcendence watch winder styled in a modern interior" loading="lazy" className="h-full w-full object-cover" />
            </Reveal>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40">
        <Reveal>
          <p className="eyebrow mb-6">How it works</p>
          <h2 className="max-w-2xl font-serif-display text-4xl font-light leading-tight tracking-tight text-[#0A0A0A] md:text-6xl">
            Rotation, on your <span className="italic text-[#B59969]">watch's terms.</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden rounded-[2px] border border-[#0A0A0A]/8 bg-[#0A0A0A]/8 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08} className="h-full">
              <div className="flex h-full flex-col justify-between gap-10 bg-[#FBFBF9] p-8 md:p-10">
                <span className="font-mono-accent text-xs text-[#B59969]">{s.n}</span>
                <div>
                  <h3 className="font-serif-display text-2xl font-medium text-[#0A0A0A]">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#525252]">{s.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SPECS */}
      <section className="border-t border-[#0A0A0A]/8 bg-[#F3F3F1]">
        <div className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="eyebrow mb-6">Specification</p>
                <h2 className="font-serif-display text-4xl font-light leading-tight tracking-tight text-[#0A0A0A] md:text-5xl">
                  Built to spec. No exceptions.
                </h2>
                <p className="mt-6 max-w-xs text-sm text-[#525252]">
                  Every specification is confirmed and locked in — each unit engineered to a precise standard and verified against our production line before launch.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <div className="flex flex-col">
                {SPECS.map(([k, v], i) => (
                  <Reveal key={k} delay={i * 0.05}>
                    <div className="grid grid-cols-1 gap-2 border-b border-[#0A0A0A]/10 py-5 sm:grid-cols-12 sm:gap-6">
                      <span className="font-mono-accent text-xs tracking-wider text-[#0A0A0A] sm:col-span-4">{k}</span>
                      <span className="text-[#525252] sm:col-span-8">{v}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA / FORM */}
      <section id="prebook" className="relative mx-auto max-w-[1400px] scroll-mt-24 px-6 py-28 md:px-12 md:py-40">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="eyebrow mb-6">Be first · Zero cost</p>
              <h2 className="font-serif-display text-5xl font-light leading-[0.95] tracking-tighter text-[#0A0A0A] md:text-7xl">
                Reserve your <span className="italic text-[#B59969]">Transcendence.</span>
              </h2>
              <p className="mt-6 max-w-md text-[#525252] md:text-lg">
                Pre-book at zero cost. Early waitlist members get first access to launch pricing and colour availability before general release. No payment today.
              </p>
              <p className="mt-8 font-serif-display text-3xl italic text-[#0A0A0A]">
                <span className="[font-variant-numeric:lining-nums_tabular-nums]"><Counter value={total} /></span> <span className="text-xl not-italic text-[#525252]">reserved so far</span>
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <div className="rounded-[3px] border border-[#0A0A0A]/8 bg-white p-8 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] md:p-12">
                <PrebookForm id="cta" onSuccess={loadStats} />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0A0A0A]">
        <div className="mx-auto flex max-w-[1400px] flex-col items-center gap-6 px-6 py-12 md:flex-row md:justify-between md:px-12">
          <img src={IMAGES.logo} alt="Transcendence" className="h-10 w-auto object-contain md:h-12" />
          <p className="eyebrow text-[#8a8a8a]">Precision Watch Winders · Made for India · Shipping September</p>
        </div>
      </footer>
    </div>
  );
}
